import { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { ScreenSize, useScreenSize } from '@/hooks/useScreenSize';

type SidebarState = {
  aboveBreakpoint: boolean | null; // user preference above breakpoint
  belowOverride: boolean | null; // temporary override below breakpoint
};

export function useResponsiveSidebar(storageKey: string, targetScreenSize: ScreenSize) {
  const [state, setState] = useLocalStorage<SidebarState>(storageKey, {
    aboveBreakpoint: null,
    belowOverride: null,
  });

  const screenSize = useScreenSize();

  const isCollapsed = () => {
    if (screenSize === 'small' && targetScreenSize === 'small') {
      return state.belowOverride ?? true;
    }
    if (screenSize === 'medium' && targetScreenSize === 'medium') {
      return state.belowOverride ?? true;
    }
    return state.aboveBreakpoint ?? false;
  };

  // Toggle sidebar
  const toggle = useCallback(() => {
    setState((prev) => {
      if (screenSize === 'small' || (screenSize === 'medium' && targetScreenSize === 'medium')) {
        // Small/medium screen: toggle belowOverride
        return { ...prev, belowOverride: !(prev.belowOverride ?? true) };
      }
      // Large screen: toggle aboveBreakpoint
      return { ...prev, aboveBreakpoint: !(prev.aboveBreakpoint ?? false) };
    });
  }, [screenSize, targetScreenSize, setState]);

  // Apply responsive state on screen size change
  const handleScreenSizeChange = useCallback(() => {
    setState((prev) => {
      // Reset belowOverride if we moved to a larger screen
      if (screenSize === 'large' && prev.belowOverride !== null) {
        return { ...prev, belowOverride: null };
      }
      return prev;
    });
  }, [screenSize, setState]);

  useEffect(() => {
    handleScreenSizeChange();
  }, [handleScreenSizeChange]);

  return { isCollapsed: isCollapsed(), toggle };
}
